import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Calendar, DollarSign, User, Building, TrendingUp, Clock, CheckCircle2, AlertCircle, FileText, MessageCircle } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { VentureDeal } from '@/hooks/useVentureDeals';
import { ProfessionalDealOverview } from '@/components/deals/ProfessionalDealOverview';
import { EnhancedDealMessaging } from '@/components/deals/EnhancedDealMessaging';

interface VentureDealDetailModalProps {
  deal: VentureDeal | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const VentureDealDetailModal: React.FC<VentureDealDetailModalProps> = ({
  deal,
  open,
  onOpenChange,
}) => {
  if (!deal) return null;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
      case 'closed':
        return 'bg-green-500';
      case 'pending':
      case 'term sheet':
        return 'bg-yellow-500';
      case 'due diligence':
      case 'legal review':
        return 'bg-blue-500';
      case 'cancelled':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getTaskStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'in_progress':
        return <Clock className="h-4 w-4 text-blue-500" />;
      case 'pending':
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-500" />;
    }
  };

  const calculateProgress = () => {
    if (!deal.tasks || deal.tasks.length === 0) return 0;
    const completedTasks = deal.tasks.filter(task => task.status === 'completed').length;
    return (completedTasks / deal.tasks.length) * 100;
  };

  const getTasksByStatus = (status: string) => {
    return deal.tasks?.filter(task => task.status === status) || [];
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>{deal.title}</span>
            <Badge className={`text-white ${getStatusColor(deal.status)}`}>
              {deal.status}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="tasks">Due Diligence</TabsTrigger>
            <TabsTrigger value="financials">Financials</TabsTrigger>
            <TabsTrigger value="timeline">Timeline</TabsTrigger>
            <TabsTrigger value="messages">
              <MessageCircle className="h-4 w-4 mr-2" />
              Messages
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            <ProfessionalDealOverview
              deal={deal}
              primaryParty={{
                name: "Venture Capital Partners",
                type: "venture",
                details: "Investment Management Division"
              }}
              secondaryParty={{
                name: deal.talent?.name || "Portfolio Company",
                type: "talent",
                details: deal.talent?.sport ? `${deal.talent.sport} Sports Enterprise` : "Sports & Entertainment Venture"
              }}
              dealType={deal.deal_type || "investment"}
            >
              {/* Investment Terms & Conditions */}
              <div className="space-y-4">
                <div>
                  <div className="font-semibold text-foreground mb-2">Investment Structure:</div>
                  <div className="text-muted-foreground">
                    <div>• Investment Type: {deal.deal_type || 'Equity Investment'}</div>
                    <div>• Investment Category: {deal.deal_category || 'Series A'}</div>
                    <div>• Due Diligence Status: {deal.tasks?.length ? `${deal.tasks.filter(t => t.status === 'completed').length}/${deal.tasks.length} items completed` : 'Pending'}</div>
                    <div>• Target IRR: To be determined based on final terms</div>
                  </div>
                </div>

                {deal.talent && (
                  <div>
                    <div className="font-semibold text-foreground mb-2">Portfolio Company Profile:</div>
                    <div className="text-muted-foreground">
                      <div>• Company Name: {deal.talent.name}</div>
                      {deal.talent.sport && <div>• Industry Sector: {deal.talent.sport} & Sports Entertainment</div>}
                      <div>• Stage: Professional sports & brand development</div>
                      <div>• Growth potential: High-value talent brand monetization</div>
                    </div>
                  </div>
                )}

                <div>
                  <div className="font-semibold text-foreground mb-2">Investment Thesis:</div>
                  <div className="text-muted-foreground">
                    <div>• Strategic investment in high-potential athlete brand</div>
                    <div>• Revenue diversification through sponsorships and partnerships</div>
                    <div>• Long-term brand value creation and monetization</div>
                    <div>• Market expansion opportunities in sports entertainment</div>
                  </div>
                </div>

                <div>
                  <div className="font-semibold text-foreground mb-2">Due Diligence Progress:</div>
                  <div className="text-muted-foreground">
                    <div className="flex items-center space-x-2 mb-2">
                      <Progress value={calculateProgress()} className="flex-1" />
                      <span className="text-sm font-medium">{Math.round(calculateProgress())}% Complete</span>
                    </div>
                    <div>• Financial review and performance analysis</div>
                    <div>• Legal documentation and compliance verification</div>
                    <div>• Market analysis and competitive positioning</div>
                    <div>• Management team assessment and background checks</div>
                  </div>
                </div>
              </div>
            </ProfessionalDealOverview>
          </TabsContent>

          <TabsContent value="tasks" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Due Diligence Tasks</CardTitle>
                <CardDescription>
                  Track progress of due diligence and closing tasks
                </CardDescription>
              </CardHeader>
              <CardContent>
                {deal.tasks && deal.tasks.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Task</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Assigned To</TableHead>
                        <TableHead>Due Date</TableHead>
                        <TableHead>Progress</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {deal.tasks.map((task) => (
                        <TableRow key={task.id}>
                          <TableCell>
                            <div className="flex items-center space-x-2">
                              {getTaskStatusIcon(task.status)}
                              <div>
                                <p className="font-medium">{task.title}</p>
                                {task.description && (
                                  <p className="text-sm text-muted-foreground">{task.description}</p>
                                )}
                                {task.is_milestone && (
                                  <Badge variant="outline" className="mt-1">Milestone</Badge>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {task.status.replace('_', ' ')}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {task.assigned_to === 'venture' ? 'Venture Team' : 'Company'}
                          </TableCell>
                          <TableCell>
                            {task.due_date && new Date(task.due_date).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center space-x-2">
                              <Progress value={task.progress_percentage} className="w-16" />
                              <span className="text-sm">{task.progress_percentage}%</span>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="text-center text-muted-foreground py-4">
                    No tasks created yet for this investment deal.
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="financials" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Financial Details</CardTitle>
                <CardDescription>
                  Investment terms and financial information
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium mb-3">Investment Terms</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Investment Amount:</span>
                        <span className="font-medium">{formatCurrency(deal.deal_value)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Investment Type:</span>
                        <span className="font-medium">{deal.deal_type}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Deal Category:</span>
                        <span className="font-medium">{deal.deal_category}</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium mb-3">Key Dates</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Deal Created:</span>
                        <span>{new Date(deal.created_at).toLocaleDateString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Target Close:</span>
                        <span>
                          {deal.estimated_completion_date 
                            ? new Date(deal.estimated_completion_date).toLocaleDateString()
                            : 'TBD'
                          }
                        </span>
                      </div>
                      {deal.actual_completion_date && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Actual Close:</span>
                          <span>{new Date(deal.actual_completion_date).toLocaleDateString()}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="timeline" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Deal Timeline</CardTitle>
                <CardDescription>
                  Key milestones and deal progression
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <div>
                      <p className="font-medium">Deal Created</p>
                      <p className="text-sm text-muted-foreground">
                        {new Date(deal.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>

                  {deal.tasks?.filter(task => task.is_milestone).map((milestone) => (
                    <div key={milestone.id} className="flex items-center space-x-3">
                      <div className={`w-2 h-2 rounded-full ${
                        milestone.status === 'completed' ? 'bg-green-500' : 'bg-gray-300'
                      }`}></div>
                      <div>
                        <p className="font-medium">{milestone.title}</p>
                        <p className="text-sm text-muted-foreground">
                          {milestone.completion_date 
                            ? `Completed: ${new Date(milestone.completion_date).toLocaleDateString()}`
                            : milestone.due_date 
                            ? `Due: ${new Date(milestone.due_date).toLocaleDateString()}`
                            : 'No date set'
                          }
                        </p>
                      </div>
                    </div>
                  ))}

                  {deal.estimated_completion_date && (
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
                      <div>
                        <p className="font-medium">Target Close Date</p>
                        <p className="text-sm text-muted-foreground">
                          {new Date(deal.estimated_completion_date).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="messages" className="space-y-4">
            <EnhancedDealMessaging dealId={deal.id} />
          </TabsContent>
        </Tabs>

        <div className="flex justify-end space-x-2 pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button>
            Edit Deal
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};